import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { FiUsers, FiPlus, FiSearch, FiEdit2, FiTrash2, FiShield, FiUser, FiCheck, FiX } from 'react-icons/fi';

/* ══ CONQUER Cyan Theme ══ */
const THEME = {
  primary: '#00B4D8',
  secondary: '#48CAE4',
  accent: '#90E0EF',
  success: '#059669',
  warning: '#D97706',
  danger: '#DC2626',
  dark: '#0A1628',
  slate: '#0096C7',
  muted: '#48CAE4',
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ username: '', email: '', password: '', role: 'user', status: 'active' });
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users?page=${page}&search=${search}`);
      setUsers(data.users);
      setTotal(data.total);
      setPages(data.pages);
    } catch { toast.error('Failed to fetch users'); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => {
    setEditing(null);
    setForm({ username: '', email: '', password: '', role: 'user', status: 'active' });
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditing(user);
    setForm({ username: user.username, email: user.email, password: '', role: user.role, status: user.status });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/admin/users/${editing._id}`, payload);
        toast.success('User updated');
      } else {
        await api.post('/admin/users', form);
        toast.success('User created');
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try { await api.delete(`/admin/users/${id}`); toast.success('User deleted'); fetchUsers(); }
    catch { toast.error('Delete failed'); }
  };

  const handleToggleStatus = async (id) => {
    try { await api.patch(`/admin/users/${id}/toggle-status`); toast.success('Status updated'); fetchUsers(); }
    catch { toast.error('Update failed'); }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 10, border: '2px solid #E0E7FF',
    fontSize: 14, color: THEME.dark, background: '#FAFAFF', outline: 'none',
  };

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: THEME.dark, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiUsers size={28} style={{ color: THEME.primary }} />
            Squad Management
          </h1>
          <p style={{ fontSize: 14, color: THEME.muted, marginTop: 6 }}>{total} team members</p>
        </div>
        <button
          onClick={openCreate}
          style={{
            padding: '12px 24px', borderRadius: 12,
            background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.secondary})`,
            color: '#fff', fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 8,
            boxShadow: `0 6px 20px ${THEME.primary}30`,
          }}
        >
          <FiPlus size={18} /> Add Member
        </button>
      </div>

      {/* Search */}
      <div style={{
        background: '#fff', borderRadius: 14, padding: 16, marginBottom: 24,
        border: `1px solid ${THEME.accent}30`, boxShadow: '0 2px 12px rgba(124,58,237,0.06)',
        display: 'flex', alignItems: 'center', gap: 12, maxWidth: 400,
      }}>
        <FiSearch size={20} style={{ color: THEME.muted }} />
        <input
          type="text" placeholder="Search members..."
          value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ ...inputStyle, border: 'none', background: 'transparent', padding: 0 }}
        />
      </div>

      {/* Table */}
      <div style={{
        background: '#fff', borderRadius: 18, overflow: 'hidden',
        border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)',
      }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <div style={{ width: 48, height: 48, border: '4px solid #E0E7FF', borderTopColor: THEME.primary, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${THEME.primary}08, ${THEME.accent}05)` }}>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase', letterSpacing: 0.5 }}>Member</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Email</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Role</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Joined</th>
                  <th style={{ padding: '16px 20px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={u._id} style={{ borderBottom: '1px solid #F0EEFF', background: i % 2 === 0 ? '#fff' : '#FAFAFF' }}>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                          width: 40, height: 40, borderRadius: 10,
                          background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.secondary})`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontWeight: 700, fontSize: 16, textTransform: 'uppercase',
                        }}>
                          {u.username?.charAt(0)}
                        </div>
                        <span style={{ fontWeight: 700, color: THEME.dark, fontSize: 14 }}>{u.username}</span>
                      </div>
                    </td>
                    <td style={{ padding: '14px 20px', color: THEME.muted, fontSize: 13 }}>{u.email}</td>
                    <td style={{ padding: '14px 20px' }}>
                      <span style={{
                        padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        background: u.role === 'admin' ? `${THEME.primary}15` : '#E0E7FF',
                        color: u.role === 'admin' ? THEME.primary : THEME.slate,
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}>
                        {u.role === 'admin' ? <FiShield size={12} /> : <FiUser size={12} />}
                        {u.role.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <span style={{
                        padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        background: u.status === 'active' ? `${THEME.success}15` : `${THEME.danger}15`,
                        color: u.status === 'active' ? THEME.success : THEME.danger,
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}>
                        {u.status === 'active' ? <FiCheck size={12} /> : <FiX size={12} />}
                        {u.status.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '14px 20px', color: THEME.muted, fontSize: 13 }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td style={{ padding: '14px 20px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => openEdit(u)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #E0E7FF', background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: THEME.slate, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <FiEdit2 size={14} /> Edit
                        </button>
                        <button onClick={() => handleToggleStatus(u._id)} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: u.status === 'active' ? `${THEME.warning}15` : `${THEME.success}15`, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: u.status === 'active' ? THEME.warning : THEME.success }}>
                          {u.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                        <button onClick={() => handleDelete(u._id)} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: `${THEME.danger}15`, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: THEME.danger, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <FiTrash2 size={14} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan="6" style={{ padding: 60, textAlign: 'center', color: THEME.muted }}>No members found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, padding: 20, borderTop: '1px solid #F0EEFF' }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #E0E7FF', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: THEME.slate }}>‹</button>
            {[...Array(pages)].map((_, i) => (
              <button key={i} onClick={() => setPage(i + 1)}
                style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: page === i + 1 ? THEME.primary : '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: page === i + 1 ? '#fff' : THEME.slate }}>{i + 1}</button>
            ))}
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #E0E7FF', background: '#fff', cursor: page === pages ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: THEME.slate }}>›</button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(30,27,75,0.5)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowModal(false)}>
          <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 480, boxShadow: '0 24px 60px rgba(124,58,237,0.2)' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '24px 28px', borderBottom: '1px solid #F0EEFF', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: THEME.dark, margin: 0 }}>{editing ? 'Edit Member' : 'Add New Member'}</h2>
              <button onClick={() => setShowModal(false)} style={{ width: 36, height: 36, borderRadius: 10, border: 'none', background: '#F0EEFF', cursor: 'pointer', fontSize: 20, color: THEME.muted }}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Username</label>
                  <input style={inputStyle} value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} required />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Email</label>
                  <input type="email" style={inputStyle} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Password {editing && '(leave blank to keep)'}</label>
                  <input type="password" style={inputStyle} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} minLength={editing ? 0 : 6} required={!editing} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Role</label>
                    <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Status</label>
                    <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>
              <div style={{ padding: '20px 28px', borderTop: '1px solid #F0EEFF', display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ padding: '12px 24px', borderRadius: 10, border: '1px solid #E0E7FF', background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: THEME.slate }}>Cancel</button>
                <button type="submit" style={{ padding: '12px 24px', borderRadius: 10, border: 'none', background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.secondary})`, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#fff', boxShadow: `0 4px 12px ${THEME.primary}30` }}>{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
